import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useProductByIdQuery } from '../../api/hooks/products';
import { CancelRounded, CheckCircle } from '@mui/icons-material';
import { ProductVariant, ProductWithVariants } from 'src/app/api/openapi';
import ProductMedia from './ProductMedia';
import { GridColDef } from '@mui/x-data-grid-premium';
import CustomDataGrid from 'src/app/components/CustomDataGrid';
import { useMemo } from 'react';

const ProductView = () => {
  const { state } = useLocation();
  const { productId, orgId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useProductByIdQuery(
    {
      orgId,
      productId,
    },
    { enabled: !state }
  );

  const product: ProductWithVariants = state || data;

  const variesByValueGetter =
    (field: string) => (value: string, row: ProductVariant) =>
      row.varies_by &&
      row.varies_by.find((variesBy) => variesBy.name === field)?.value;

  const variantTableColumns: GridColDef<ProductVariant>[] = useMemo(
    () => [
      {
        field: 'price',
        headerName: 'Price',
        renderCell: ({ row }: { row: ProductVariant }) => (
          <>
            $
            {row.suggested_prices?.length
              ? row.suggested_prices[0].amount
              : '-'}
          </>
        ),
        flex: 1,
      },
      {
        field: 'gtin',
        headerName: 'GTIN',
        flex: 1,
      },
      {
        field: 'sku',
        headerName: 'SKU',
        flex: 1,
      },
      ...(product.varies_by || []).map((property) => ({
        field: property,
        renderHeader: () => (
          <Typography fontWeight={700} textTransform="capitalize">
            {property}
          </Typography>
        ),
        valueGetter: variesByValueGetter(property),
        flex: 1,
      })),
    ],
    [product]
  );

  if (!product && isLoading) return <CircularProgress />;

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h1">{product.title}</Typography>
        <Box>
          <Typography variant="caption" textTransform="capitalize" mr={2}>
            Product ID: {product.id}
          </Typography>
          <Button variant="flat" onClick={() => navigate('./edit', { state })}>
            Edit Product
          </Button>
        </Box>
      </Stack>
      <Typography variant="caption">{product.subtitle}</Typography>
      <ProductMedia product={product} viewMode={true} />
      <Card sx={{ my: 2 }}>
        <CardHeader title="Description" />
        <Typography variant="body1" pl={3} pb={3}>
          {product.description || 'No Description'}
        </Typography>
      </Card>
      <Grid container width="50%" justifyContent="space-between" mb={2}>
        <Grid item>
          <Typography variant="body1" fontWeight={700}>
            Category
          </Typography>
          <Typography variant="body1" gutterBottom>
            {product.google_product_category?.name || 'None'}
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            Adult Considerations
          </Typography>
          <Typography variant="body1" gutterBottom>
            {product.has_adult_consideration?.split(/(?=[A-Z])/).join(' ') ||
              'None'}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" fontWeight={700}>
            EBT Eligible
          </Typography>
          <Typography variant="body1" gutterBottom>
            {product.is_ebt_eligible ? (
              <CheckCircle color="success" />
            ) : (
              <CancelRounded color="error" />
            )}
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            Published
          </Typography>
          <Typography variant="body1" gutterBottom>
            {product.is_published ? (
              <CheckCircle color="success" />
            ) : (
              <CancelRounded color="error" />
            )}
          </Typography>
        </Grid>
      </Grid>
      <Typography variant="h4">Variants</Typography>
      <Grid item xs={12}>
        <CustomDataGrid
          rows={product.variants}
          columns={variantTableColumns}
          hideFooter
        />
      </Grid>
    </>
  );
};

export default ProductView;
